import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ArticleChangeRequestResponse,
  CreateArticleChangeRequestInput,
  ItemResponse,
  ListResponse,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

/**
 * Änderungsanträge am Artikelstamm (VB-26016, ADR 0089).
 *
 * Die Technik darf Artikel bearbeiten, wirksam wird die Änderung aber erst nach
 * Freigabe durch das Sekretariat. Solange ein Antrag offen ist, ist der Artikel
 * für direkte Änderungen gesperrt — `useArticleLock` liefert genau diesen Stand
 * für die Oberfläche.
 */
const KEY = ['article-change-requests'] as const;

export function useArticleChangeRequests(
  filter: { status?: string } = {},
  /**
   * Für Abnehmer, die nicht auf einer Seite sitzen — das Abzeichen in der
   * Seitenleiste braucht Recht-Gating und einen Nachlade-Takt. Die Adresse soll
   * trotzdem nur hier stehen.
   */
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  const query = filter.status ? `?status=${filter.status}` : '';
  return useQuery({
    queryKey: [...KEY, filter.status ?? 'ALL'],
    queryFn: () =>
      apiRequest<ListResponse<ArticleChangeRequestResponse>>(`/v1/article-change-requests${query}`),
    ...options,
  });
}

/**
 * Offener Antrag zu genau einem Artikel — die Sperre in Formular und
 * Zusatzmaterial-Reiter. `undefined` heißt „noch nicht geladen", `null` „frei".
 */
export function useArticleLock(articleId: string | undefined) {
  const query = useQuery({
    queryKey: [...KEY, 'article', articleId],
    enabled: Boolean(articleId),
    queryFn: () =>
      apiRequest<ListResponse<ArticleChangeRequestResponse>>(
        `/v1/article-change-requests?status=PENDING&articleId=${articleId}`,
      ),
  });
  return {
    ...query,
    /** Der offene Antrag, der den Artikel sperrt — `null`, wenn keiner offen ist. */
    pending: query.data ? (query.data.data[0] ?? null) : undefined,
  };
}

export function useCreateArticleChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateArticleChangeRequestInput) =>
      apiRequest<ItemResponse<ArticleChangeRequestResponse>>('/v1/article-change-requests', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

export function useApproveArticleChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ArticleChangeRequestResponse>>(
        `/v1/article-change-requests/${id}/approve`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEY });
      // Mit der Freigabe ändert sich der Artikel selbst — Liste und Detail neu laden.
      queryClient.invalidateQueries({ queryKey: ['articles'] });
    },
  });
}

export function useRejectArticleChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      apiRequest<ItemResponse<ArticleChangeRequestResponse>>(
        `/v1/article-change-requests/${id}/reject`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}

/** Eigenen Antrag zurückziehen — löst die Sperre am Artikel wieder. */
export function useWithdrawArticleChangeRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<ItemResponse<ArticleChangeRequestResponse>>(
        `/v1/article-change-requests/${id}/withdraw`,
        { method: 'POST' },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
}
